import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ChevronLeft, ChevronRight, Loader2, Check, Clock, Truck, ChefHat, CalendarCheck, AlertTriangle,
} from 'lucide-react';
import { staffService } from '../../services/staffService';
import { toast } from 'sonner';
import {
  addDays, fmtDate, fmtDateLong, fmtTime, hoursLabel, todayStr, weekStartOf,
} from '../../lib/scheduleFormat';

// Mobile-first: phone-width cards, week then day, one obvious action per card.
// A driver card leads with the pickup time and the delivery date; a kitchen
// card leads with the kitchen and the shift window.
const ShiftCard = ({ shift, onConfirm, busyId }) => {
  const mine = shift.is_mine;
  return (
    <Card
      className={`${mine ? 'border-l-4 border-l-amber-500' : 'opacity-80'} ${shift.conflict ? 'bg-red-50/60' : ''}`}
      data-testid={`staff-shift-${shift.id}`}
    >
      <CardContent className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            {shift.kitchen_bound ? (
              <>
                <p className="text-base font-semibold text-gray-900">
                  {fmtTime(shift.start_time)} – {fmtTime(shift.end_time)}
                </p>
                <p className="text-sm text-gray-600">
                  <ChefHat className="mr-1 inline h-4 w-4 text-amber-600" />
                  {shift.position_label}{shift.kitchen_name ? ` · ${shift.kitchen_name}` : ''}
                </p>
              </>
            ) : (
              <>
                <p className="text-base font-semibold text-gray-900">
                  Pickup {fmtTime(shift.pickup_time)}
                </p>
                <p className="text-sm text-gray-600">
                  <Truck className="mr-1 inline h-4 w-4 text-sky-600" />
                  Delivering {fmtDate(shift.delivery_date)} · shift {fmtTime(shift.start_time)}–{fmtTime(shift.end_time)}
                </p>
              </>
            )}
            <p className="mt-1 text-sm font-medium text-gray-800">
              {mine ? 'You' : (shift.user_name || 'Open shift')}
            </p>
            {shift.previous && (
              <p className="mt-1 text-xs text-gray-500" data-testid={`staff-shift-previous-${shift.id}`}>
                Changed from <s>{fmtTime(shift.previous.start_time)}–{fmtTime(shift.previous.end_time)}</s>
                {shift.previous.pickup_time && <> · pickup was <s>{fmtTime(shift.previous.pickup_time)}</s></>}
                {' '}— please reconfirm
              </p>
            )}
            {mine && shift.notes && <p className="mt-1 text-xs text-gray-600">{shift.notes}</p>}
          </div>

          <div className="flex flex-col items-end gap-2">
            {shift.confirmed ? (
              <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-800">
                <Check className="mr-1 h-3 w-3" /> Confirmed
              </Badge>
            ) : (
              <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">
                <Clock className="mr-1 h-3 w-3" /> {shift.needs_reconfirm ? 'Reconfirm' : 'Unconfirmed'}
              </Badge>
            )}
            {shift.conflict && (
              <Badge variant="outline" className="border-red-300 bg-white text-red-700">
                <AlertTriangle className="mr-1 h-3 w-3" /> Check with the office
              </Badge>
            )}
          </div>
        </div>

        {mine && !shift.confirmed && (
          <Button
            className="mt-3 h-11 w-full bg-amber-600 hover:bg-amber-700"
            onClick={() => onConfirm(shift)}
            disabled={busyId === shift.id}
            data-testid={`staff-confirm-${shift.id}`}
          >
            {busyId === shift.id ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Check className="mr-1 h-4 w-4" />}
            I&apos;ll be there
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

const Schedule = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [weekStart, setWeekStart] = useState(() => weekStartOf(searchParams.get('week') || todayStr()));
  const [week, setWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmingWeek, setConfirmingWeek] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setWeek(await staffService.getMySchedule(weekStart));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load your schedule');
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  const goto = (next) => {
    setWeekStart(next);
    setSearchParams({ week: next });
  };

  const confirm = async (shift) => {
    setBusyId(shift.id);
    try {
      await staffService.confirmShift(shift.id);
      toast.success('Thanks — confirmed');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not confirm that shift');
    } finally {
      setBusyId(null);
    }
  };

  const confirmAll = async () => {
    setConfirmingWeek(true);
    try {
      const result = await staffService.confirmScheduleWeek(weekStart);
      toast.success(`Confirmed ${result.confirmed} shift${result.confirmed === 1 ? '' : 's'}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not confirm the week');
    } finally {
      setConfirmingWeek(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="staff-schedule">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My schedule</h1>
        <p className="mt-1 text-sm text-gray-600">
          The published week for the whole team. You can confirm your own shifts.
          {week?.timezone && <> All times are {week.timezone}.</>}
        </p>
      </div>

      <Card>
        <CardContent className="flex items-center justify-between gap-2 p-3">
          <Button variant="outline" size="icon" className="h-11 w-11"
            onClick={() => goto(addDays(weekStart, -7))}
            aria-label="Previous week" data-testid="staff-prev-week">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="text-center">
            <p className="font-semibold text-gray-900" data-testid="staff-week-label">
              {fmtDate(weekStart)} – {fmtDate(addDays(weekStart, 6))}
            </p>
            <p className="text-xs text-gray-500" data-testid="staff-week-summary">
              {week ? `${week.my_shift_count} shift${week.my_shift_count === 1 ? '' : 's'} · ${hoursLabel(week.my_hours)}` : '—'}
            </p>
          </div>
          <Button variant="outline" size="icon" className="h-11 w-11"
            onClick={() => goto(addDays(weekStart, 7))}
            aria-label="Next week" data-testid="staff-next-week">
            <ChevronRight className="h-5 w-5" />
          </Button>
        </CardContent>
      </Card>

      {week?.my_unconfirmed_count > 0 && (
        <Button
          className="h-12 w-full bg-amber-600 hover:bg-amber-700"
          onClick={confirmAll}
          disabled={confirmingWeek}
          data-testid="staff-confirm-week"
        >
          {confirmingWeek
            ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            : <CalendarCheck className="mr-1 h-5 w-5" />}
          Confirm all {week.my_unconfirmed_count} shift{week.my_unconfirmed_count === 1 ? '' : 's'} this week
        </Button>
      )}

      {loading ? (
        <div className="flex min-h-[40vh] items-center justify-center" data-testid="staff-schedule-loading">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      ) : !week?.published ? (
        <Card>
          <CardContent className="p-8 text-center" data-testid="staff-schedule-empty">
            <p className="font-medium text-gray-900">Nothing published for this week yet.</p>
            <p className="mt-1 text-sm text-gray-600">
              The office publishes the rota ahead of time — you will get an email as soon as it is out.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-5">
          {week.days.filter((day) => day.shifts.length > 0).map((day) => (
            <div key={day.date} data-testid={`staff-day-${day.date}`}>
              <h2 className="mb-2 text-base font-semibold text-gray-900 md:text-lg">
                {fmtDateLong(day.date)}
              </h2>
              <div className="space-y-2">
                {day.shifts.map((shift) => (
                  <ShiftCard key={shift.id} shift={shift} onConfirm={confirm} busyId={busyId} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Schedule;
